/**
 * Arena layout — the SINGLE source of truth for the arena's static boxes.
 *
 * Each {@link LayoutBox} carries both the gameplay surface (`surface`, used
 * for server + client collision via {@link getArenaAABBs}) and the render
 * styling (`render` material variant + `tint`, used by the client's
 * `apps/fps/src/scene/Arena.ts`). Before this module the geometry lived
 * imperatively in two places that had to be hand-synced; now both consumers
 * derive from one flat, data-driven list — which the in-browser map editor
 * (`?editor=1`) reads and writes.
 *
 * The committed list lives in `arena.layout.data.ts` (a plain array literal so
 * the editor can rewrite it). {@link buildDefaultArenaLayout} is how that file
 * was first generated from the original hand-written geometry, kept here so the
 * default can always be regenerated.
 */
import type { SurfaceMaterial } from "./arenaGeometry";
import { ARENA_LAYOUT_DATA } from "./arena.layout.data";

/**
 * One static box of the arena, defined by its CENTER + full SIZE (metres) —
 * the same convention the old `box()`/`rect()` helpers used. Converted to a
 * min/max {@link ArenaAABB} for collision and to a render mesh for display.
 */
export interface LayoutBox {
  /** Stable unique id (used by the editor for selection + diffing). */
  id: string;
  /** Center position. */
  cx: number; cy: number; cz: number;
  /** Full size along each axis. */
  w: number; h: number; d: number;
  /** Gameplay/collision material (what the server + bullets care about). */
  surface: SurfaceMaterial;
  /** Render material variant when it differs from `surface` (e.g. textured
   *  "wall"/"pillar"/"midfloor"). Defaults to `surface` for rendering. */
  render?: SurfaceMaterial;
  /** Per-box render tint (multiplies the material's diffuse). */
  tint?: [number, number, number];
  /** Skip rendering (collision-only box). */
  invisible?: boolean;
}

/** The live layout the game + editor use. Sourced from the editable data file. */
export const ARENA_LAYOUT: LayoutBox[] = ARENA_LAYOUT_DATA;

// ── Default-layout generator ────────────────────────────────────────────────
// Reproduces the original arena geometry (the numbers that used to live in
// getArenaAABBs() + Arena.ts MAP_BLOCKS). Run via scripts to (re)generate
// arena.layout.data.ts. Not used at runtime — the data file is the source.

const MAP_W = 80;
const MAP_D = 50;
const MAP_H = 12;
const HALF_W = MAP_W / 2;
const HALF_D = MAP_D / 2;
const WALL_T = 1.0;

const PILLAR_CT_X = +10;
const PILLAR_W = 6;
const MID_PLAT_W = 12;
const MID_PLAT_H = 1.5;
const LOCKER_W = 8;
const LOCKER_H = 4;
const LOCKER_Z = 0;

interface BoxOpts {
  render?: SurfaceMaterial;
  tint?: [number, number, number];
  invisible?: boolean;
}

function mk(id: string, cx: number, cy: number, cz: number, w: number, h: number, d: number, surface: SurfaceMaterial, opts: BoxOpts = {}): LayoutBox {
  const b: LayoutBox = { id, cx, cy, cz, w, h, d, surface };
  if (opts.render && opts.render !== surface) b.render = opts.render;
  if (opts.tint) b.tint = opts.tint;
  if (opts.invisible) b.invisible = true;
  return b;
}

function lockerPartitions(side: "T" | "CT"): LayoutBox[] {
  const isT = side === "T";
  const tag = isT ? "t" : "ct";
  const xCenter = isT ? -HALF_W + LOCKER_W / 2 + WALL_T / 2 : HALF_W - LOCKER_W / 2 - WALL_T / 2;
  const partitionLen = LOCKER_W + WALL_T;
  const zN = LOCKER_Z - LOCKER_W / 2;
  const zS = LOCKER_Z + LOCKER_W / 2;
  const openX = isT ? -HALF_W + partitionLen : HALF_W - partitionLen;
  const lintelH = 1.0;
  const lintelCenterY = LOCKER_H - lintelH / 2;
  const opts: BoxOpts = { render: "wood", tint: [0.5, 0.35, 0.2] };
  return [
    mk(`locker-${tag}-n`, xCenter, LOCKER_H / 2, zN, partitionLen, LOCKER_H, WALL_T, "wood", opts),
    mk(`locker-${tag}-s`, xCenter, LOCKER_H / 2, zS, partitionLen, LOCKER_H, WALL_T, "wood", opts),
    mk(`locker-${tag}-roof`, xCenter, LOCKER_H + 0.1, LOCKER_Z, partitionLen, 0.2, LOCKER_W, "wood", opts),
    mk(`locker-${tag}-lintel`, openX, lintelCenterY, LOCKER_Z, WALL_T, lintelH, LOCKER_W, "wood", opts),
  ];
}

function windowWall(idBase: string, cx: number, cz: number, w: number, h: number, d: number, winLength: number, winH: number, winY: number, surface: SurfaceMaterial, opts: BoxOpts): LayoutBox[] {
  const isXAligned = w > d;
  const wallLen = isXAligned ? w : d;
  const thickness = isXAligned ? d : w;
  const sideLen = (wallLen - winLength) / 2;
  const topH = h - winY - winH;
  return [
    // Sill
    mk(`${idBase}-sill`, cx, winY / 2, cz, isXAligned ? winLength : thickness, winY, isXAligned ? thickness : winLength, surface, opts),
    // Lintel
    mk(`${idBase}-lintel`, cx, h - topH / 2, cz, isXAligned ? winLength : thickness, topH, isXAligned ? thickness : winLength, surface, opts),
    // Side 1
    mk(`${idBase}-side1`,
      isXAligned ? cx - wallLen / 2 + sideLen / 2 : cx, h / 2, isXAligned ? cz : cz - wallLen / 2 + sideLen / 2,
      isXAligned ? sideLen : thickness, h, isXAligned ? thickness : sideLen, surface, opts),
    // Side 2
    mk(`${idBase}-side2`,
      isXAligned ? cx + wallLen / 2 - sideLen / 2 : cx, h / 2, isXAligned ? cz : cz + wallLen / 2 - sideLen / 2,
      isXAligned ? sideLen : thickness, h, isXAligned ? thickness : sideLen, surface, opts),
  ];
}

/** Regenerate the original arena geometry as a flat {@link LayoutBox} list. */
export function buildDefaultArenaLayout(): LayoutBox[] {
  const out: LayoutBox[] = [];

  // Perimeter walls (textured "wall", concrete collision).
  const wallOpts: BoxOpts = { render: "wall" };
  out.push(mk("perimeter-n", 0, MAP_H / 2, -HALF_D, MAP_W, MAP_H, WALL_T, "concrete", wallOpts));
  out.push(mk("perimeter-s", 0, MAP_H / 2, HALF_D, MAP_W, MAP_H, WALL_T, "concrete", wallOpts));
  out.push(mk("perimeter-w", -HALF_W, MAP_H / 2, 0, WALL_T, MAP_H, MAP_D, "concrete", wallOpts));
  out.push(mk("perimeter-e", HALF_W, MAP_H / 2, 0, WALL_T, MAP_H, MAP_D, "concrete", wallOpts));

  // Locker partitions (one per side, centered).
  out.push(...lockerPartitions("T"));
  out.push(...lockerPartitions("CT"));

  // Mid pillars: CT solid block; T pillar is a hollow concrete room (rusty
  // "pillar" texture on both).
  const pillarOpts: BoxOpts = { render: "pillar" };
  out.push(mk("pillar-ct", PILLAR_CT_X, 6, 0, PILLAR_W, 12, PILLAR_W, "concrete", pillarOpts));
  out.push(mk("troom-n", -10, 6, -2.6, 6, 12, 0.8, "concrete", pillarOpts));
  out.push(mk("troom-s", -10, 6, 2.6, 6, 12, 0.8, "concrete", pillarOpts));
  out.push(mk("troom-w-n", -12.6, 6, -2, 0.8, 12, 2, "concrete", pillarOpts));
  out.push(mk("troom-w-s", -12.6, 6, 2, 0.8, 12, 2, "concrete", pillarOpts));
  out.push(mk("troom-door-lintel", -12.6, 7.5, 0, 0.8, 9, 2, "concrete", pillarOpts));
  out.push(mk("troom-e-sill", -7.4, 0.45, 0, 0.8, 0.9, 6, "concrete", pillarOpts));
  out.push(mk("troom-e-lintel", -7.4, 6.95, 0, 0.8, 10.1, 6, "concrete", pillarOpts));

  // Mid raised platform.
  out.push(mk("mid-platform", 0, MID_PLAT_H / 2, 0, MID_PLAT_W, MID_PLAT_H, MID_PLAT_W, "concrete", { render: "midfloor" }));

  // Cover crates (one per side, centered).
  const crateOpts: BoxOpts = { tint: [0.6, 0.45, 0.25] };
  out.push(mk("crate-t", -20, 0.6, 0, 2.5, 1.2, 2.5, "wood", crateOpts));
  out.push(mk("crate-ct", 20, 0.6, 0, 2.5, 1.2, 2.5, "wood", crateOpts));

  // Mid box quads (4 small blocks around the mid platform).
  const MID_CORNER = 2.5;
  const quadOpts: BoxOpts = { tint: [0.5, 0.5, 0.5] };
  let qi = 0;
  for (const [cx, cz] of [
    [-MID_CORNER, -MID_CORNER],
    [+MID_CORNER, -MID_CORNER],
    [-MID_CORNER, +MID_CORNER],
    [+MID_CORNER, +MID_CORNER],
  ] as Array<[number, number]>) {
    out.push(mk(`mid-quad-${qi++}`, cx * 2, 0.6, cz * 2, 2.5, 1.2, 1.5, "concrete", quadOpts));
  }

  // Gate walls (full-height, x=±16, two pieces each leaving a central gap).
  const gateOpts: BoxOpts = { tint: [0.45, 0.45, 0.5] };
  out.push(mk("gate-w-n", -16, 6, -11.5, 1, 12, 13, "concrete", gateOpts));
  out.push(mk("gate-w-s", -16, 6, 11.5, 1, 12, 13, "concrete", gateOpts));
  out.push(mk("gate-e-n", 16, 6, -11.5, 1, 12, 13, "concrete", gateOpts));
  out.push(mk("gate-e-s", 16, 6, 11.5, 1, 12, 13, "concrete", gateOpts));

  // Corridor blocks (N + S).
  const corrOpts: BoxOpts = { tint: [0.4, 0.4, 0.4] };
  const corrWoodOpts: BoxOpts = { tint: [0.5, 0.35, 0.2] };
  out.push(mk("corridor-nw", -15, 1.5, -20.75, 4, 3, 1.5, "concrete", corrOpts));
  out.push(mk("corridor-ne", 15, 1.5, -20.75, 4, 3, 1.5, "concrete", corrOpts));
  out.push(mk("corridor-n-mid", 0, 1.0, -20.25, 2.5, 2, 2.5, "wood", corrWoodOpts));
  out.push(mk("corridor-sw", -15, 1.5, 20.75, 4, 3, 1.5, "concrete", corrOpts));
  out.push(mk("corridor-se", 15, 1.5, 20.75, 4, 3, 1.5, "concrete", corrOpts));
  out.push(mk("corridor-s-mid", 0, 1.0, 20.25, 2.5, 2, 2.5, "wood", corrWoodOpts));

  // South window-walls — textured with the OSB wood diffuse (the "shooting
  // window" walls). Render variant "woodwall"; collision stays concrete.
  const winOpts: BoxOpts = { render: "woodwall" };
  out.push(...windowWall("window-sw", -7, 13, 1, 2.5, 5, 2, 1.0, 0.9, "concrete", winOpts));
  out.push(...windowWall("window-se", 7, 18, 1, 2.5, 5, 2, 1.0, 0.9, "concrete", winOpts));

  // North window-walls (rotated 90° — long axis along X).
  out.push(...windowWall("window-nw", -7, -20.5, 5, 2.5, 1, 2, 1.0, 0.9, "concrete", winOpts));
  out.push(...windowWall("window-ne", 7, -20.5, 5, 2.5, 1, 2, 1.0, 0.9, "concrete", winOpts));

  // North inverted-L + dog-door (crouch-only opening).
  out.push(mk("dogdoor-leg", -6.5, 1.25, -10, 1, 2.5, 6, "concrete", corrOpts));
  out.push(mk("dogdoor-w", -5.875, 1.25, -7, 1.25, 2.5, 1, "concrete", corrOpts));
  out.push(mk("dogdoor-e", -3.125, 1.25, -7, 1.25, 2.5, 1, "concrete", corrOpts));
  out.push(mk("dogdoor-lintel", -4.5, 1.9, -7, 1.5, 1.2, 1, "concrete", corrOpts));

  return out;
}
